import { useEffect, useRef, useState } from 'react'

import {
  decodeAlert,
  decodeFleet,
  decodeMetrics,
  decodeSnapshot,
  parseJson,
} from './parse.ts'
import { useSseConnection } from './useSseConnection.ts'
import type {
  AlertPayload,
  ConnectionState,
  FleetPayload,
  MetricsPayload,
  SseEventName,
  ThroughputPoint,
} from './types.ts'

export const STREAM_URL = '/api/admin/stream/metrics'

/** Backend ticks `metrics` every 2s. Five missed ticks is not idleness. */
export const STALE_AFTER_MS = 10_000

/** Visible window of the throughput chart. */
export const THROUGHPUT_WINDOW_MS = 30 * 60 * 1000

/**
 * Hard ceiling on retained points. The window prune below is the real policy;
 * this exists so a backend sending bogus timestamps still cannot grow the array
 * without bound over an 8-hour shift.
 */
export const THROUGHPUT_MAX_POINTS = 1_000

/**
 * Alert rail is a live tail, not an archive.
 *
 * Dimensionado pela retenção do próprio servidor: o `snapshot` traz até 100
 * alertas abertos, e um buffer menor descartaria parte do que acabou de chegar
 * — o painel abriria já mentindo sobre quantos alertas existem.
 */
export const ALERT_BUFFER = 100

export interface MetricsStreamState {
  connection: ConnectionState
  metrics: MetricsPayload | null
  fleet: FleetPayload | null
  /** Rolling 30-minute series, oldest first, deduped by timestamp. */
  throughput: ThroughputPoint[]
  /** Newest first. */
  alerts: AlertPayload[]
  /** Server's clock from the last payload — shown instead of the browser's. */
  serverTime: string | null
  /** Browser clock (epoch ms) when the last event of any kind arrived. */
  lastEventAt: number | null
  /** Per-stream freshness:  ticks every 10s, so its data is older by
   *  design and panels fed by it say so instead of implying 2s freshness. */
  lastMetricsAt: number | null
  lastFleetAt: number | null
  lastEventName: SseEventName | null
  /** Diagnostics surfaced in the footer so these claims stay auditable. */
  diagnostics: StreamDiagnostics
}

export interface StreamDiagnostics {
  snapshots: number
  reconnects: number
  /** Payloads the decoder refused. */
  malformed: number
  /** Points evicted from the rolling window — proof the buffer is bounded. */
  pointsDropped: number
  seriesLength: number
  openedAt: number | null
}

/**
 * The store owned by the metrics hook.
 *
 * Everything the connection machine cares about — the state, the freshness clock,
 * the reconnect count — now lives in `useSseConnection`. What is left here is the
 * decoded telemetry and the two bounded buffers.
 */
interface Store {
  metrics: MetricsPayload | null
  fleet: FleetPayload | null
  throughput: ThroughputPoint[]
  alerts: AlertPayload[]
  serverTime: string | null
  lastMetricsAt: number | null
  lastFleetAt: number | null
  lastEventName: SseEventName | null
  snapshots: number
  malformed: number
  pointsDropped: number
  seriesLength: number
}

const INITIAL: Store = {
  metrics: null,
  fleet: null,
  throughput: [],
  alerts: [],
  serverTime: null,
  lastMetricsAt: null,
  lastFleetAt: null,
  lastEventName: null,
  snapshots: 0,
  malformed: 0,
  pointsDropped: 0,
  seriesLength: 0,
}

/**
 * Merge incoming points into the rolling window.
 *
 * Three jobs, in order: replace-or-append by timestamp (a re-sent point must not
 * duplicate), keep chronological order (the backend is not required to), and
 * evict anything outside the window. Returns the same array reference when
 * nothing changed so `React.memo` on the chart actually bites.
 */
export function mergeThroughput(
  current: readonly ThroughputPoint[],
  incoming: readonly ThroughputPoint[],
): { series: ThroughputPoint[]; dropped: number } {
  if (incoming.length === 0) return { series: current as ThroughputPoint[], dropped: 0 }

  const byTime = new Map<number, ThroughputPoint>()
  for (const point of current) byTime.set(Date.parse(point.t), point)
  for (const point of incoming) byTime.set(Date.parse(point.t), point)

  const sorted = [...byTime.entries()].sort((a, b) => a[0] - b[0])
  const newest = sorted.at(-1)?.[0] ?? 0
  const floor = newest - THROUGHPUT_WINDOW_MS

  let kept = sorted.filter(([time]) => time >= floor)
  if (kept.length > THROUGHPUT_MAX_POINTS) kept = kept.slice(kept.length - THROUGHPUT_MAX_POINTS)

  // Evictions only — duplicate timestamps collapsing is not a drop.
  const dropped = sorted.length - kept.length
  return { series: kept.map(([, point]) => point), dropped }
}

function pushAlert(current: readonly AlertPayload[], alert: AlertPayload): AlertPayload[] {
  const deduped = current.filter((existing) => existing.id !== alert.id)
  return [alert, ...deduped].slice(0, ALERT_BUFFER)
}

/**
 * Merge the snapshot's retained alerts into the rail.
 *
 * The snapshot sends them oldest first; the rail is newest first. `id` is
 * lexicographically ordered by occurrence, which is exactly why the contract
 * says so — sorting by it needs no date parsing and survives two alerts inside
 * the same millisecond.
 */
export function hydrateAlerts(
  current: readonly AlertPayload[],
  incoming: readonly AlertPayload[],
): AlertPayload[] {
  if (incoming.length === 0) return current as AlertPayload[]

  const byId = new Map<string, AlertPayload>()
  for (const alert of current) byId.set(alert.id, alert)
  // Snapshot wins on a repeated id: it is the server's current view of it.
  for (const alert of incoming) byId.set(alert.id, alert)

  return [...byId.values()].sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)).slice(0, ALERT_BUFFER)
}

/**
 * The metrics stream hook.
 *
 * Consome o núcleo `useSseConnection`, que é dono da conexão, da máquina de
 * estado e da recuperação. Aqui ficam os decodificadores e os dois buffers
 * limitados. O contrato de vivacidade é o do stream de métricas: um payload que o
 * decodificador recusa NÃO conta como heartbeat — ele nem marca `live` nem
 * alimenta o watchdog. Um backend emitindo lixo a cada 2 s dessatura a tela em
 * 10 s como qualquer silêncio, em vez de mostrar números congelados sob um crachá
 * verde de "ao vivo", e o contador de malformados no rodapé diz qual dos dois
 * está acontecendo.
 */
export function useMetricsStream(url: string = STREAM_URL): MetricsStreamState {
  const [store, setStore] = useState<Store>(INITIAL)

  // O núcleo pergunta isto na abertura para escolher entre 'connecting' e
  // 'stale'. Espelha `metrics !== null` sem forçar o núcleo a conhecer o store.
  const hasDataRef = useRef(false)
  useEffect(() => {
    hasDataRef.current = store.metrics !== null
  }, [store.metrics])

  const bumpMalformed = (): void =>
    setStore((prev) => ({ ...prev, malformed: prev.malformed + 1 }))

  const core = useSseConnection({
    url,
    staleAfterMs: STALE_AFTER_MS,
    hasData: () => hasDataRef.current,
    events: {
      snapshot: (raw, at) => {
        const snapshot = decodeSnapshot(parseJson(raw), new Date(at).toISOString())
        if (snapshot === null) {
          bumpMalformed()
          return false
        }
        setStore((prev) => {
          // Reconnect semantics: replace, never merge. A merged state can hold a
          // silent agent that was resolved while we were disconnected.
          const merged = mergeThroughput([], snapshot.metrics.throughput_series)
          return {
            ...prev,
            metrics: snapshot.metrics,
            fleet: snapshot.fleet,
            throughput: merged.series,
            // Alerts are the exception to "replace, never merge": the snapshot
            // carries what is still OPEN, and the rail also holds alerts that
            // fired and resolved while we were connected. Dedupe by id keeps the
            // reconnect from re-announcing what is already on screen.
            alerts: hydrateAlerts(prev.alerts, snapshot.alerts),
            serverTime: snapshot.metrics.server_time,
            lastMetricsAt: at,
            lastFleetAt: at,
            lastEventName: 'snapshot',
            snapshots: prev.snapshots + 1,
            seriesLength: merged.series.length,
          }
        })
        return true
      },
      metrics: (raw, at) => {
        const metrics = decodeMetrics(parseJson(raw))
        if (metrics === null) {
          bumpMalformed()
          return false
        }
        setStore((prev) => {
          const merged = mergeThroughput(prev.throughput, metrics.throughput_series)
          return {
            ...prev,
            // The series lives in the store, not in the payload: a tick carries
            // one point and must not shrink the chart to a single sample.
            metrics: { ...metrics, throughput_series: [] },
            throughput: merged.series,
            serverTime: metrics.server_time,
            lastMetricsAt: at,
            lastEventName: 'metrics',
            pointsDropped: prev.pointsDropped + merged.dropped,
            seriesLength: merged.series.length,
          }
        })
        return true
      },
      fleet: (raw, at) => {
        const fleet = decodeFleet(parseJson(raw))
        if (fleet === null) {
          bumpMalformed()
          return false
        }
        setStore((prev) => ({ ...prev, fleet, lastFleetAt: at, lastEventName: 'fleet' }))
        return true
      },
      alert: (raw, at) => {
        const alert = decodeAlert(parseJson(raw), new Date(at).toISOString())
        if (alert === null) {
          bumpMalformed()
          return false
        }
        setStore((prev) => ({
          ...prev,
          alerts: pushAlert(prev.alerts, alert),
          lastEventName: 'alert',
        }))
        return true
      },
    },
  })

  return {
    connection: core.connection,
    metrics: store.metrics,
    fleet: store.fleet,
    throughput: store.throughput,
    alerts: store.alerts,
    serverTime: store.serverTime,
    lastEventAt: core.lastEventAt,
    lastMetricsAt: store.lastMetricsAt,
    lastFleetAt: store.lastFleetAt,
    lastEventName: store.lastEventName,
    diagnostics: {
      snapshots: store.snapshots,
      reconnects: core.reconnects,
      malformed: store.malformed,
      pointsDropped: store.pointsDropped,
      seriesLength: store.seriesLength,
      openedAt: core.openedAt,
    },
  }
}
